/* eslint-disable lingui/no-unlocalized-strings */
import { t, Trans } from "@lingui/macro";
import {
  ChartBarIcon,
  ChatCircleDotsIcon,
  FireIcon,
  LinkedinLogoIcon,
} from "@phosphor-icons/react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

const TOOLS = [
  {
    title: "ATS Resume Checker",
    description:
      "Paste any job description and your resume. Get a match score, missing keywords, and rewrite suggestions — free.",
    href: "/tools/ats-checker",
    icon: ChartBarIcon,
    accent: "bg-accent-soft text-accent",
  },
  {
    title: "Resume Roaster",
    description:
      "Upload your PDF and get a brutally honest, AI-powered roast of your resume. Five free roasts per day.",
    href: "/tools/resume-roast",
    icon: FireIcon,
    accent: "bg-warning-soft text-warning-strong",
  },
  {
    title: "LinkedIn Endorsement Writer",
    description:
      "Generate a professional LinkedIn recommendation in seconds. Just paste the profile and pick a tone.",
    href: "/tools/linkedin-endorsement",
    icon: LinkedinLogoIcon,
    accent: "bg-info-soft text-info-strong",
  },
  {
    title: "LinkedIn Profile Review",
    description:
      "Get AI feedback on your LinkedIn headline, summary, and experience section — with actionable rewrites.",
    href: "/tools/linkedin-profile-review",
    icon: ChatCircleDotsIcon,
    accent: "bg-accent-soft text-accent",
  },
] as const;

export const ToolsPage = () => (
  <div className="bg-background text-foreground">
    {/*
      canonical is not decoration here — it is what makes this page exist.

      tools/scripts/ssg-snapshot.mjs refuses to write a snapshot whose <head>
      has no canonical (ssg-head.mjs findHeadDefect), so a bare <Helmet> title
      like the one this page used to render would fail the build rather than
      ship. And without a snapshot, main.ts falls back to index.html — which is
      the HOMEPAGE snapshot — so /tools returned 200 with a body byte-identical
      to "/", same <title>, same canonical `https://apply-edge.com/`, for every
      crawler that followed the "Free Tools" link in the SEO-page footer.
    */}
    <SEO
      canonical="/tools"
      description={t`Free AI-powered career tools: ATS resume checker, resume roaster, LinkedIn endorsement writer, and profile review.`}
      title={t`Free AI Career Tools — Apply Edge`}
    />

    <section className="mx-auto max-w-[1280px] px-6 pb-24 pt-16">
      <div className="max-w-[52ch]">
        <div className="text-xs font-medium text-muted-foreground">Free tools</div>
        <h1 className="mt-3 text-balance text-[36px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[48px]">
          AI career tools — no account needed.
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
          Every tool below is free to use with daily limits. No sign-up required. Create an account
          to unlock higher limits.
        </p>
      </div>

      <div className="mt-12 grid gap-4 sm:grid-cols-2">
        {TOOLS.map((tool) => (
          <Link
            key={tool.href}
            className="group flex flex-col rounded-2xl border border-border bg-surface p-6 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md"
            to={tool.href}
          >
            <div className="flex items-center gap-3">
              <div className={`grid size-10 place-items-center rounded-xl ${tool.accent}`}>
                <tool.icon size={20} weight="duotone" />
              </div>
              <h2 className="text-[18px] font-semibold tracking-tight">{tool.title}</h2>
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
              {tool.description}
            </p>
            <div className="mt-4 inline-flex min-h-11 items-center font-mono text-[11px] text-accent transition-colors group-hover:text-foreground sm:block sm:min-h-0">
              <Trans>Try it free →</Trans>
            </div>
          </Link>
        ))}
      </div>
    </section>
  </div>
);
